import React, { useEffect, useRef, useState } from "react";
import { PSFS_StatPortalProps, PSFSElement } from "./Psfs_Slice";
import { FoundationsButton } from "../../GenericComponents/button";
import DatePicker from "../../GenericComponents/DatePicker";
import PatientHeader from "./PatientHeader";
import CommentBox, { useCommentBox } from "./Comments";
import { getAge } from "./PSFS_StatPortal";

declare let window: any
const UnicornApp = window.unicornInstance as any
import moment from "moment-timezone";

export const formatPhoneNumber = (value: string | number | null | undefined): string => {
    if (!value) return "";
    // Remove all non-digit characters
    const digits = String(value).replace(/\D/g, "");

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
        return digits;
    } else if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
};

// Format SSN as xxx-xx-xxxx
export const formatSSN = (value: string | number | null | undefined): string => {
    if (!value) return "";
    const digits = String(value).replace(/\D/g, "");
    if (digits.length <= 3) {
        return digits;
    } else if (digits.length <= 5) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
    }
};

const NTTCReview: React.FC<PSFS_StatPortalProps> = ({ form, setForm, handleStepStatusAndSave, PSFSElements, stepManuallyChanged, handleStatusResetAndClose, handleSave }) => {
    const formRef = useRef(null);
    const [isCustomerSite] = useState<boolean>(UnicornApp.checkUserPrivileges("NTSPAdmin"))
    const timeSelectRef = React.useRef<HTMLSelectElement>(null);
    const [errors, setErrors] = useState<{ [s: string]: string }>({})
    // --- Date and Time State Management ---
    const [appointmentDate, setAppointmentDate] = React.useState<Date | null>(() => {
        if (!form.ts_Appointment) return null;
        // Parse date in Eastern timezone and extract date components
        const momentDate = moment.tz(form.ts_Appointment, "America/New_York");
        // Create Date object using date components to avoid timezone offset issues
        return new Date(momentDate.year(), momentDate.month(), momentDate.date());
    });
    const [appointmentTimeKey, setAppointmentTimeKey] = React.useState<string>(() => {
        if (!form.ts_Appointment) return "-1";
        const momentDate = moment.tz(form.ts_Appointment, "America/New_York");
        const timeString = momentDate.format("HH:mm");
        const match = PSFSElements?.PSFSTime?.find((t: any) => t.FriendlyName === timeString);
        return match?._key !== undefined ? String(match._key) : "-1";
    });

    // Check if this step is already completed (form has moved past this step)
    const currentStepNo = PSFSElements?.PSFSStep?.find(step => step.ETLMatchValue === "NTTCReview")?.StepNo ?? 1;
    const isStepCompleted = !stepManuallyChanged && form.fk_Step !== undefined && form.fk_Step !== -1 && form.fk_Step > currentStepNo && (form.fk_Step == 1 ? form.fk_Status != -1 : true);

    // Generic change handler for all fields
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        let processedValue = value;

        if (name === 'PatientCellNumber') {
            processedValue = value.replace(/\D/g, '').slice(0, 10); // Only digits, max 10
        } else if (name === 'PatientSSN' || name === 'FullSSN') {
            processedValue = value.replace(/\D/g, '').slice(0, 9); // Only digits, max 9
        }

        if (name === 'PatientDOB') {
            // calculate age from DOB
            const calculatedAge = value ? getAge(value) : 0;
            setForm(prev => ({
                ...prev,
                PatientDOB: processedValue,
                PatientAge: calculatedAge && !isNaN(calculatedAge) ? calculatedAge : 0
            }));
            return;
        } else if (name === 'PatientCellNumber') {
            const digits = value.replace(/\D/g, '');
            if (digits.length != 10 && !!value) {
                setErrors(prev => ({
                    ...prev,
                    PatientCellNumber: 'Cell number must be exactly 10 digits.'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    PatientCellNumber: ''
                }));
            }
        }

        setForm(prev => ({
            ...prev,
            [name]: processedValue
        }));
    };


    // --- Effect: Keep state in sync with form.ts_Appointment ---
    React.useEffect(() => {
        if (form.ts_Appointment) {
            const momentDate = moment.tz(form.ts_Appointment, "America/New_York");
            // Create Date object using date components to avoid timezone offset issues
            setAppointmentDate(new Date(momentDate.year(), momentDate.month(), momentDate.date()));
            const timeString = momentDate.format("HH:mm");
            const match = PSFSElements?.PSFSTime?.find((t: any) => t.FriendlyName === timeString);
            setAppointmentTimeKey(match?._key !== undefined ? String(match._key) : "-1");
        } else {
            setAppointmentDate(null);
            setAppointmentTimeKey("-1");
        }
    }, [form.ts_Appointment]);

    // --- Handler: Combine date and time, update form.ts_Appointment ---
    const handleAppointmentChange = (date: Date | null, timeKey: string) => {
        setAppointmentDate(date);
        setAppointmentTimeKey(timeKey);

        const timeObj = PSFSElements?.PSFSTime?.find((t: any) => t._key == timeKey);

        let combinedString: string | null = null;

        if (date && timeObj) {
            // Both date and time selected
            const [hours, minutes] = timeObj.FriendlyName.split(":").map(Number);
            // Extract date components directly from Date object to avoid timezone issues
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            
            const m = moment.tz(dateStr, "YYYY-MM-DD", "America/New_York")
                .set({ hour: hours, minute: minutes, second: 0 });
            combinedString = m.format("YYYY-MM-DD HH:mm");
        } else if (date) {
            // Only date selected
            // Extract date components directly from Date object to avoid timezone issues
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            
            const m = moment.tz(dateStr, "YYYY-MM-DD", "America/New_York");
            combinedString = m.format("YYYY-MM-DD");
        } else if (timeObj) {
            // Only time selected (rare case)
            const [hours, minutes] = timeObj.FriendlyName.split(":").map(Number);
            const m = moment.tz("America/New_York").set({ hour: hours, minute: minutes, second: 0 });
            combinedString = m.format("HH:mm");
        }

        setForm((prev: any) => ({
            ...prev,
            ts_Appointment: combinedString,
        }));
    };


    const validateDOB = (dateString: string): string => {
        const momentDate = moment.tz(dateString, ["MM/DD/YYYY", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"], 'America/New_York');
        if (!momentDate.isValid()) return '';
        const now = moment.tz('America/New_York');
        if (momentDate.isAfter(now, 'day')) {
            return 'DOB cannot be in the future';
        }
        return '';
    };

    useEffect(() => {
        if (form.CaseId) {
            setErrors({});
        }

        if (form.PatientCellNumber) {
            const digits = form.PatientCellNumber.replace(/\D/g, '');
            if (digits.length != 10) {
                setErrors(prev => ({
                    ...prev,
                    PatientCellNumber: 'Cell number must be exactly 10 digits.'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    PatientCellNumber: ''
                }));
            }
        }
    }, [form.CaseId, form.PatientCellNumber])

    // Handler for date field validation errors from DatePicker
    const handleDateValidationError = (fieldName: string) => (error: string) => {
        setErrors((prev: { [s: string]: string }) => ({
            ...prev,
            [fieldName]: error,
        }))
    }

    return (
        <>
            <form
                className="psfs-form-container"
                ref={formRef}
            >
                <div className="psfs-container mb-1">
                    <div className="first-child mb-4">
                        <label className="psfs-main-label">NTTC Review</label>
                        <div className="form-group mt-3">
                            <label htmlFor="FullSSN">Patient SSN</label>
                            <span>
                                <input
                                    type="text"
                                    className="psfs-fields"
                                    id="FullSSN"
                                    name="FullSSN"
                                    value={formatSSN(form.FullSSN)}
                                    onChange={handleChange}
                                    tabIndex={2}
                                    maxLength={11}
                                    placeholder="xxx-xx-xxxx"
                                />
                            </span>
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="PatientCellNumber">Patient Cell Number</label>
                            <span>
                                <input
                                    type="text"
                                    className="psfs-fields"
                                    id="PatientCellNumber"
                                    name="PatientCellNumber"
                                    value={formatPhoneNumber(form.PatientCellNumber)}
                                    onChange={handleChange}
                                    placeholder="(555) 555-5555"
                                    maxLength={14}
                                    tabIndex={2}
                                    onBeforeInput={(e: React.FormEvent<HTMLInputElement> & InputEvent) => {
                                        if (!/[0-9]/.test((e as InputEvent).data ?? "")) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                                {errors.PatientCellNumber && (
                                    <div
                                        style={{
                                            color: 'red',
                                            position: 'absolute',
                                            lineHeight: '1',
                                            fontSize: "smaller",
                                            margin: '3px',
                                        }}
                                    >
                                        {errors.PatientCellNumber + ''}
                                    </div>
                                )}
                            </span>
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="ts_Appointment">PSFS Appointment <br /></label>
                            <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', width: '250px' }}>
                                <DatePicker
                                    id="ts_Appointment"
                                    name="ts_Appointment"
                                    dateFormat="MM/dd/yyyy"
                                    tabIndex={4}
                                    showMasking={true}
                                    className="appointment-date-picker"
                                    inputClass="appointment-date-picker"
                                    showTime={false}
                                    startingDate={appointmentDate}
                                    setStartingDate={(value: string) => {
                                        // Parse date string directly without timezone conversion
                                        // DatePicker uses the date components as-is
                                        const date = value
                                            ? new Date(value)
                                            : null;

                                        // If date is cleared, also clear time
                                        const timeKey = date ? appointmentTimeKey : "-1";

                                        handleAppointmentChange(date, timeKey);

                                        setTimeout(() => {
                                            timeSelectRef.current?.focus();
                                        }, 100);

                                        if (!date) {
                                            setErrors(prev => ({ ...prev, appointmentTime: "" }));
                                        } else if (timeKey === "-1") {
                                            setErrors(prev => ({ ...prev, appointmentTime: "Please select a time for the appointment." }));
                                        } else {
                                            setErrors(prev => ({ ...prev, appointmentTime: "" }));
                                        }
                                    }}
                                    placeholder="Select date"
                                    isClearable={true}
                                    calendarClosed={false}
                                    style={{ width: '50%', }}
                                    dateFormatCalendar="MMMM yyyy"
                                    monthsShown={2}
                                    onValidationError={handleDateValidationError('ts_Appointment')}
                                />
                                <select
                                    ref={timeSelectRef}
                                    className="psfs-fields appointment-date-picker"
                                    id="appointmentTime"
                                    name="appointmentTime"
                                    value={appointmentTimeKey}
                                    onChange={e => {
                                        handleAppointmentChange(appointmentDate, e.target.value);
                                        // Clear error if a valid time is selected
                                        if (e.target.value !== "-1") {
                                            setErrors(prev => ({ ...prev, appointmentTime: "" }));
                                        }
                                    }}
                                    tabIndex={5}
                                    style={{ width: '50%', padding: '0 10px', margin: '0px', cursor: appointmentDate ? 'pointer' : 'not-allowed' }}
                                    disabled={!appointmentDate && !errors.ts_Appointment}
                                >
                                    <option value="-1" disabled>-Select-</option>
                                    {PSFSElements?.PSFSTime?.map((time: any) => (
                                        <option key={time._key} value={time._key}>
                                            {time.FriendlyName}
                                        </option>
                                    ))}
                                </select>
                                {errors.appointmentTime && (
                                    <div
                                        style={{
                                            color: 'red',
                                            position: 'absolute',
                                            lineHeight: '1',
                                            fontSize: "smaller",
                                            margin: '3px',
                                            marginTop: '58px'
                                        }}
                                    >
                                        {errors.appointmentTime + ''}
                                    </div>
                                )}
                                {errors.ts_Appointment && (
                                    <div
                                        style={{
                                            color: 'red',
                                            position: 'absolute',
                                            lineHeight: '1',
                                            fontSize: "smaller",
                                            margin: '3px',
                                            marginTop: '58px'
                                        }}
                                    >
                                        {errors.ts_Appointment + ''}
                                    </div>
                                )}
                            </span>
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="PatientDOB">Patient DOB <br /></label>
                            <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', width: '250px' }}>
                                <DatePicker
                                    id="PatientDOB"
                                    name="PatientDOB"
                                    dateFormat="MM/dd/yyyy"
                                    tabIndex={15}
                                    //className="ntsp-fields"
                                    showTime={false}
                                    showMasking={true}
                                    startingDate={
                                        form.PatientDOB
                                            ? (() => {
                                                // Accept ISO or custom format
                                                const m = moment(form.PatientDOB, ["MM/DD/YYYY", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]);
                                                if (!m.isValid()) return null;
                                                return new Date(m.year(), m.month(), m.date());
                                            })()
                                            : null
                                    }
                                    setStartingDate={(dateString) => {
                                        // Handle empty date (cleared DatePicker)
                                        if (!dateString) {
                                            setForm(prev => ({
                                                ...prev,
                                                PatientDOB: null,
                                                PatientAge: 0,
                                            }));
                                            setErrors(prev => ({
                                                ...prev,
                                                PatientDOB: ''
                                            }));
                                            return;
                                        }

                                        const errorMsg = validateDOB(dateString);
                                        setErrors(prev => ({
                                            ...prev,
                                            PatientDOB: errorMsg
                                        }));
                                        if (!errorMsg) {
                                            const calculatedAge = getAge(dateString);
                                            setForm(prev => ({
                                                ...prev,
                                                PatientDOB: dateString,
                                                PatientAge: calculatedAge && !isNaN(calculatedAge) ? calculatedAge : 0,
                                            }));
                                        }
                                    }}
                                    className="appointment-date-picker"
                                    inputClass="appointment-date-picker"
                                    placeholder="Select DOB"
                                    isClearable={true}
                                    calendarClosed={true}
                                    style={{ width: '66%', }}
                                    onValidationError={handleDateValidationError('PatientDOB')}
                                />
                                <span>/</span>
                                <input
                                    type="number"
                                    id="PatientAge"
                                    name="PatientAge"
                                    value={form.PatientAge}
                                    onChange={handleChange}
                                    tabIndex={16}
                                    placeholder="age"
                                    className="psfs-fields appointment-date-picker"
                                    style={{ width: '40%', maxWidth: '65px' }}
                                    disabled
                                />

                                {errors.PatientDOB && (
                                    <div
                                        style={{
                                            color: 'red',
                                            position: 'absolute',
                                            lineHeight: '1',
                                            fontSize: "smaller",
                                            margin: '3px',
                                            marginTop: '58px'
                                        }}
                                    >
                                        {errors.PatientDOB + ''}
                                    </div>
                                )}
                            </span>
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="fk_TSP">
                                Tablet Service Provider <br />
                                <span style={{ fontSize: '11px', color: '#50b4ff' }}>
                                    <a href={PSFSElements?.PSFSUrl?.find?.((u: any) => u.FriendlyName === "TabletServiceProvider")?.Url} target="_blank" rel="noopener noreferrer">(Click Here to Check Address)</a>
                                </span>
                            </label>
                            <span>
                                <select
                                    className="psfs-fields"
                                    id="fk_TSP"
                                    name="fk_TSP"
                                    value={form.fk_TSP}
                                    onChange={handleChange}
                                    tabIndex={5}
                                >
                                    <option value="-1">-Select-</option>
                                    {PSFSElements?.TSP?.sort((a, b) => a._key - b._key).map((TSP: PSFSElement) => (
                                        <option key={TSP._key} value={TSP._key}>
                                            {TSP.FriendlyName}
                                        </option>
                                    ))}
                                </select>
                            </span>
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="fk_ContactNational">
                                Contact National Tablet team to Order <br />
                                <span style={{ fontSize: '11px', color: '#50b4ff' }}>
                                    <a href={PSFSElements?.PSFSUrl?.find?.((u: any) => u.FriendlyName === "TabletOrder")?.Url} target="_blank" rel="noopener noreferrer">(Click Here to Order)</a>
                                </span><br />
                                SET ENCRYPTION ON EMAIL!
                            </label>
                            <span>
                                <select
                                    className="psfs-fields"
                                    id="fk_ContactNational"
                                    name="fk_ContactNational"
                                    value={form.fk_ContactNational}
                                    onChange={handleChange}
                                    tabIndex={6}
                                >
                                    <option value="-1">-Select-</option>
                                    {PSFSElements?.YesNo?.sort((a, b) => a._key - b._key).map((YesNo: PSFSElement) => (
                                        <option key={YesNo._key} value={YesNo._key}>
                                            {YesNo.FriendlyName}
                                        </option>
                                    ))}
                                </select>
                            </span>
                        </div>
                        <div className="form-group mt-3">
                            <label>
                                <span style={{ fontWeight: '600' }}>Annie Enrollment </span>
                                <a href={PSFSElements?.PSFSUrl?.find?.((u: any) => u.FriendlyName === "AnnieEnrollment")?.Url} tabIndex={4} target="_blank" rel="noopener noreferrer">(Click Here to Enroll)</a>
                            </label>
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="fk_AnnieTextMessages">
                                Veteran has a cellphone and <br />
                                agrees to participate in Annie <br />
                                text messages?
                            </label>
                            <span>
                                <select
                                    className="psfs-fields"
                                    id="fk_AnnieTextMessages"
                                    name="fk_AnnieTextMessages"
                                    value={form.fk_AnnieTextMessages}
                                    onChange={handleChange}
                                    tabIndex={7}
                                >
                                    <option value="-1">-Select-</option>
                                    {PSFSElements?.YesNo?.sort((a, b) => a._key - b._key).map((YesNo: PSFSElement) => (
                                        <option key={YesNo._key} value={YesNo._key}>
                                            {YesNo.FriendlyName}
                                        </option>
                                    ))}
                                </select>
                            </span>
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="AppointmentReservedInTMP">Appointment Reserved in TMP?</label>
                            <span>
                                <select
                                    className="psfs-fields"
                                    id="fk_AppointmentReservedInTMP"
                                    name="fk_AppointmentReservedInTMP"
                                    value={form.fk_AppointmentReservedInTMP}
                                    onChange={handleChange}
                                    tabIndex={8}
                                >
                                    <option value="-1">-Select-</option>
                                    {PSFSElements?.YesNo?.sort((a, b) => a._key - b._key).map((YesNo: PSFSElement) => (
                                        <option key={YesNo._key} value={YesNo._key}>
                                            {YesNo.FriendlyName}
                                        </option>
                                    ))}
                                </select>
                            </span>
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="DocumentOnConsultInCPRS">Document on consult in CPRS</label>
                            <span>
                                <select
                                    className="psfs-fields"
                                    id="fk_DocumentOnConsultInCPRS"
                                    name="fk_DocumentOnConsultInCPRS"
                                    value={form.fk_DocumentOnConsultInCPRS}
                                    onChange={handleChange}
                                    tabIndex={9}
                                >
                                    <option value="-1">-Select-</option>
                                    {PSFSElements?.YesNo?.sort((a, b) => a._key - b._key).map((YesNo: PSFSElement) => (
                                        <option key={YesNo._key} value={YesNo._key}>
                                            {YesNo.FriendlyName}
                                        </option>
                                    ))}
                                </select>
                            </span>
                        </div>
                        <div className="form-group psfs-buttons mt-10">
                            {!isStepCompleted && (
                                <span>
                                    {PSFSElements?.PSFSStatus
                                        .filter((status: any) => status.FriendlyName === "Devices Ordered")
                                        .map((status: any) => (
                                            <span key={status._key}>
                                                <FoundationsButton
                                                    id={status._key}
                                                    buttonClasses="psfs-green-button"
                                                    onClick={() => handleStepStatusAndSave("NTTCReview", status._key, "TMPScheduling")}
                                                    disabled={Object.values(errors).some(Boolean) || !isCustomerSite}
                                                    tabIndex={10}
                                                >
                                                    {status.FriendlyName}
                                                </FoundationsButton>
                                            </span>
                                        ))}
                                </span>
                            )}
                            <span>
                                <FoundationsButton
                                    id="-1"
                                    tabIndex={11}
                                    buttonClasses="app-button"
                                    onClick={() => handleSave(false)}
                                    disabled={Object.values(errors).some(Boolean)}
                                >
                                    Save
                                </FoundationsButton>
                            </span>
                            {!isStepCompleted && (
                                <>
                                    <span>
                                        {PSFSElements?.PSFSStatus
                                            .filter((status: any) => status.FriendlyName === "Veteran Declined Appt.")
                                            .map((status: any) => (
                                                <span key={status._key}>
                                                    <FoundationsButton
                                                        id={status._key}
                                                        tabIndex={12}
                                                        buttonClasses="psfs-red-button"
                                                        disabled={Object.values(errors).some(Boolean) || !isCustomerSite}
                                                        onClick={() => handleStatusResetAndClose("NTTCReview", status._key, "TMPScheduling")}
                                                    >
                                                        {status.FriendlyName}
                                                    </FoundationsButton>
                                                </span>
                                            ))}
                                    </span>
                                    <span>
                                        {PSFSElements?.PSFSStatus
                                            .filter((status: any) => status.FriendlyName === "Clinically Canceled")
                                            .map((status: any) => (
                                                <span key={status._key}>
                                                    <FoundationsButton
                                                        id={status._key}
                                                        tabIndex={13}
                                                        buttonClasses="psfs-red-button"
                                                        disabled={Object.values(errors).some(Boolean) || !isCustomerSite}
                                                        onClick={() => handleStatusResetAndClose("NTTCReview", status._key, "TMPScheduling")}
                                                    >
                                                        {status.FriendlyName}
                                                    </FoundationsButton>
                                                </span>
                                            ))}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    {/* <CommentBox {...commentBoxProps} minHeight="615px" /> */}
                </div>
            </form>
        </>
    );
}

export default NTTCReview;